<template>
    <Layout>
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-header">
                        <h3 class="mb-0 title--screen">抽選 編集</h3>
                    </div>
                    <div class="card-body">
                        <!-- Loading State -->
                        <div v-if="isLoadingBasic" class="text-center py-5">
                            <i class="ri-loader-4-line" style="font-size: 2rem; display: inline-block; animation: spin 1s linear infinite"></i>
                            <p class="mt-2">データを読み込んでいます。</p>
                        </div>

                        <!-- Edit View -->
                        <div v-else>
                            <ul class="nav nav-tabs mb-4">
                                <li class="nav-item">
                                    <a
                                        class="nav-link font-weight-bold"
                                        :class="{ active: currentTab === 'basic' }"
                                        href="javascript:void(0)"
                                        @click="currentTab = 'basic'"
                                    >
                                        基本情報登録
                                    </a>
                                </li>
                                <li class="nav-item">
                                    <a
                                        class="nav-link font-weight-bold"
                                        :class="{ active: currentTab === 'setting', disabled: !lotteryId }"
                                        href="javascript:void(0)"
                                        @click="lotteryId && (currentTab = 'setting')"
                                    >
                                        抽選設定
                                    </a>
                                </li>
                                <li v-if="checkPermission('lottery.analytics')" class="nav-item">
                                    <a
                                        class="nav-link font-weight-bold"
                                        :class="{ active: currentTab === 'result', disabled: !lotteryId }"
                                        href="javascript:void(0)"
                                        @click="lotteryId && (currentTab = 'result')"
                                    >
                                        抽選結果
                                    </a>
                                </li>
                            </ul>

                            <!-- TAB 1: Basic Info -->
                            <div v-if="currentTab === 'basic'">
                                <div class="form-group row">
                                    <label class="col-sm-2 col-form-label">タイトル <span class="text-danger">*</span></label>
                                    <div class="col-sm-10">
                                        <input
                                            v-model="form.title"
                                            type="text"
                                            class="form-control"
                                            :class="{ 'is-invalid': errors.title }"
                                            placeholder=""
                                        />
                                        <div v-if="errors.title" class="invalid-feedback" style="display: block">{{ errors.title }}</div>
                                    </div>
                                </div>
                                <div v-if="registrationType === 'internal'" class="form-group row">
                                    <label class="col-sm-2 col-form-label">フォーム選択 <span class="text-danger">*</span></label>
                                    <div class="col-sm-10">
                                        <Multiselect
                                            id="select-form"
                                            :value.sync="form.form_id"
                                            :options="forms"
                                            :config="{ trackBy: 'id', label: 'name' }"
                                            :class="{ 'is-invalid': errors.form_id }"
                                        />
                                        <div v-if="errors.form_id" class="invalid-feedback" style="display: block">{{ errors.form_id }}</div>
                                    </div>
                                </div>
                                <div v-if="registrationType === 'external'" class="form-group row">
                                    <label class="col-sm-2 col-form-label">ファイルアップロード<span class="text-danger">*</span></label>
                                    <div class="col-sm-10">
                                        <UploadsFiles
                                            id="upload-file-external"
                                            :title="'ファイルアップロード'"
                                            classLabel="btn-secondary"
                                            typeUpload="import"
                                            :config="{ allowedExtension: ['csv', 'xlsx'], allowedSize: 204800 }"
                                            @importFile="handleFileUpload"
                                            :fileValues="activeImportFile"
                                            @removeFile="handleRemoveFile"
                                        />
                                        <small class="text-muted" style="font-size: 13px">※アップロード可能なファイル形式：.csv, .xlsx</small>
                                        <div v-if="errors.file" class="invalid-feedback" style="display: block">{{ errors.file }}</div>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-sm-2 col-form-label">登録タグ</label>
                                    <div class="col-sm-10">
                                        <KeyWordTag id="registration-tags" checkPre="hash" :model.sync="form.tags" :config.sync="configKeyword" />
                                    </div>
                                </div>
                            </div>

                            <!-- TAB 2: Setting -->
                            <div v-if="currentTab === 'setting'">
                                <SettingTab
                                    ref="settingTab"
                                    :lottery-id="lotteryId"
                                    :list-block-form="listBlockForm"
                                    :rewards.sync="rewards"
                                    :is-loading-setting="isSaving"
                                    :is-editing.sync="isEditingReward"
                                />
                            </div>

                            <!-- TAB 3: Result -->
                            <div v-if="currentTab === 'result'">
                                <ResultTab
                                    ref="resultTab"
                                    :lottery-id="lotteryId"
                                    :list-block-form="listBlockForm"
                                    :rewards.sync="rewards"
                                    :post-draw-config.sync="globalPostDrawDuplicateConfig"
                                    :full-data-dup.sync="globalFullDataDuplicateEnabled"
                                    @submit-then-draw="handleSubmitThenDraw"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Footer v-if="currentTab === 'basic' || currentTab === 'setting'">
            <div class="d-flex justify-content-center align-items-center" style="gap: 1rem">
                <button
                    v-if="currentTab === 'setting' && isEditingReward"
                    type="button"
                    class="btn btn-common-cancel"
                    @click="$refs.settingTab.cancelEditReward()"
                >
                    キャンセル
                </button>
                <button v-else type="button" class="btn btn-common-cancel" @click="$router.push('/lottery/list')">キャンセル</button>

                <button
                    v-if="currentTab === 'setting' && isEditingReward"
                    type="button"
                    class="btn btn-common btn-success"
                    @click="$refs.settingTab.saveEditReward()"
                >
                    抽選設定を保存
                </button>
                <button v-else type="button" class="btn btn-common btn-success" :disabled="isSaving" @click="saveAll(false)">
                    <span v-if="isSaving"><i class="ri-loader-4-line"></i> 処理中...</span>
                    <span v-else>保存</span>
                </button>
            </div>
        </Footer>
    </Layout>
</template>

<script>
import Layout from '@/views/layouts/main';
import appConfig from '@/app.config';
import KeyWordTag from '@/components/KeywordTag/main.vue';
import UploadsFiles from '@/components/Uploads/Files/main.vue';
import Multiselect from '@/components/Multiselect/main.vue';
import SettingTab from '../components/SettingTab.vue';
import ResultTab from '../components/ResultTab.vue';
import Footer from '@/components/Footer/main.vue';
import { managerFormMethods, lotteryMethods } from '@/state/helpers';
import { showMessage } from '@/utils/messages';
import { checkPermission } from '@/utils/handlers';

export default {
    page: {
        title: '抽選 編集',
        meta: [{ name: 'description', content: appConfig.description }]
    },
    components: {
        UploadsFiles,
        Layout,
        KeyWordTag,
        Multiselect,
        SettingTab,
        ResultTab,
        Footer
    },
    data() {
        return {
            registrationType: null,
            currentTab: 'basic',
            lotteryId: null,

            // Basic form
            form: { title: '', form_id: null, file: null, tags: [] },
            errors: {},
            configKeyword: { arrayTag: [], placeholder: '', isShowLable: false, isCheckDuplicate: true },
            forms: [],
            isLoadingBasic: true,

            // Shared between tabs
            rewards: [],
            listBlockForm: [],
            isSaving: false,
            isEditingReward: false,
            activeImportFile: [],

            globalPostDrawDuplicateConfig: null,
            globalFullDataDuplicateEnabled: false
        };
    },
    async created() {
        await this.fetchForms();
        if (this.$route.query.id) {
            this.lotteryId = this.$route.query.id;
            this.fetchLotteryDetail();
        }
    },
    watch: {
        'form.title'(val) {
            if (!val) {
                this.$set(this.errors, 'title', 'タイトルを入力してください。');
            } else if (val.length > 255) {
                this.$set(this.errors, 'title', 'タイトルを255文字以内で入力してください。');
            } else {
                this.$delete(this.errors, 'title');
            }
        },
        'form.form_id'(val) {
            if (this.registrationType === 'internal' && !val) {
                this.$set(this.errors, 'form_id', 'フォームを選択してください。');
            } else {
                this.$delete(this.errors, 'form_id');
            }
        },
        'form.file'(val) {
            if (this.registrationType === 'external' && !val && (!this.activeImportFile || this.activeImportFile.length === 0)) {
                this.$set(this.errors, 'file', 'ファイルをアップロードしてください。');
            } else {
                this.$delete(this.errors, 'file');
            }
        },
        activeImportFile(val) {
            if (this.registrationType === 'external' && !this.form.file && (!val || val.length === 0)) {
                this.$set(this.errors, 'file', 'ファイルをアップロードしてください。');
            } else {
                this.$delete(this.errors, 'file');
            }
        },
        registrationType() {
            this.$delete(this.errors, 'form_id');
            this.$delete(this.errors, 'file');
        }
    },
    methods: {
        ...managerFormMethods,
        ...lotteryMethods,
        checkPermission,

        async fetchForms() {
            try {
                const res = await this.getListManagerForm('page=1&limit=9999&sorts[id]=desc');
                if (res && res.data) this.forms = Array.isArray(res.data) ? res.data : res.data.data || [];
            } catch (e) {
                console.error(e);
            }
        },

        selectType(type) {
            this.registrationType = type;
            this.currentTab = 'basic';
        },

        handleFileUpload(file) {
            this.form.file = file;
        },

        handleRemoveFile() {
            this.form.file = null;
            this.activeImportFile = [];
        },

        async fetchLotteryDetail() {
            try {
                this.isLoadingBasic = true;
                const res = await this.detailLottery(this.lotteryId);
                const data = res?.data || res;
                if (data) {
                    this.form.title = data.title;
                    if (this.forms.length === 0) await this.fetchForms();
                    this.form.form_id = this.forms.find((f) => f.id == data.form_id) || data.form_id;
                    this.form.tags = data.tags || [];
                    this.configKeyword.arrayTag = [...this.form.tags];
                    this.registrationType = data.source_type === 'external' || data.source_type === 'csv' ? 'external' : 'internal';

                    if (data.source_type === 'external' || data.source_type === 'csv') {
                        if (data.active_import_file) {
                            this.activeImportFile = [
                                {
                                    ...data.active_import_file,
                                    fileSize: Math.round((data.active_import_file.file_size || 0) / 1024)
                                }
                            ];
                        }
                        await this.fetchExternalColumns();
                    } else if (data.form_id) {
                        const blocks = await this.getListBlockOfForm({ id: data.form_id, query: 'exclude_deleted=true' });
                        this.listBlockForm = (blocks || [])
                            .sort((a, b) => a.order_index - b.order_index)
                            .map((b) => ({
                                ...b,
                                options: b.list_options,
                                submit_results: []
                            }));
                    }

                    if (data.post_draw_duplicate_config) {
                        this.globalPostDrawDuplicateConfig = data.post_draw_duplicate_config;
                    }
                    this.globalFullDataDuplicateEnabled = data.full_data_duplicate_enabled || false;

                    if (data.reward_config && data.reward_config.rewards) {
                        this.rewards = data.reward_config.rewards.map((r) => {
                            const itemBlocks = [];
                            if (r.condition_config?.filter_groups?.[0]?.filter_blocks) {
                                r.condition_config.filter_groups[0].filter_blocks.forEach((fb) => {
                                    const block_item = this.listBlockForm.find((b) => b.id == fb.block_id);
                                    if (block_item) {
                                        const conditionMap = [
                                            { id: 1, value: 'equal', name: 'イコール' },
                                            { id: 2, value: 'start_with', name: '前方一致' },
                                            { id: 3, value: 'end_with', name: '後方一致' },
                                            { id: 4, value: 'include', name: '含む' },
                                            { id: 5, value: 'no_answer', name: '回答なし' },
                                            { id: 6, value: 'has_answer', name: '回答あり' },
                                            { id: 7, value: 'select_one', name: 'いずれか選択' },
                                            { id: 7, value: 'equal_choice', name: '等しい' },
                                            { id: 8, value: 'select_all', name: 'すべて選択' },
                                            { id: 8, value: 'not_equal_choice', name: '等しくない' },
                                            { id: 9, value: 'gt_equal', name: 'より後' },
                                            { id: 9, value: 'more_than', name: '以上' },
                                            { id: 10, value: 'lt_equal', name: 'より前' },
                                            { id: 10, value: 'less_than', name: '以下' }
                                        ];
                                        const conditions = conditionMap.find((c) => c.value === fb.condition_type) || null;
                                        const item = {
                                            block_item,
                                            conditions,
                                            select_value_block: null,
                                            value_block: null,
                                            value_phone_1: null,
                                            value_phone_2: null,
                                            value_phone_3: null,
                                            value_postal_code_1: null,
                                            value_postal_code_2: null,
                                            value_first_name: null,
                                            value_last_name: null
                                        };
                                        if (['radio', 'checkbox', 'select', 'select_box_province'].includes(block_item.block_type)) {
                                            if (Array.isArray(fb.value)) {
                                                item.select_value_block = fb.value.map((v) => ({ value: v, value_search: v }));
                                            } else {
                                                item.select_value_block = { value: fb.value, value_search: fb.value };
                                            }
                                        } else if (block_item.block_type === 'postal_code') {
                                            const parts = String(fb.value || '').split('-');
                                            item.value_postal_code_1 = parts[0] || '';
                                            item.value_postal_code_2 = parts[1] || '';
                                        } else if (block_item.block_type === 'phone_number') {
                                            const parts = String(fb.value || '').split('-');
                                            item.value_phone_1 = parts[0] || '';
                                            item.value_phone_2 = parts[1] || '';
                                            item.value_phone_3 = parts[2] || '';
                                        } else if (block_item.block_type === 'full_name') {
                                            const parts = String(fb.value || '').split(' ');
                                            item.value_first_name = parts[0] || '';
                                            item.value_last_name = parts.slice(1).join(' ') || '';
                                        } else {
                                            item.value_block = fb.value;
                                        }
                                        itemBlocks.push(item);
                                    }
                                });
                            }

                            const preDrawFields =
                                r.pre_draw_duplicate_config?.fields?.map((fId) => {
                                    return this.listBlockForm.find((b) => b.id == fId) || { id: fId, block_name: 'Unknown Field' };
                                }) || [];

                            const postDrawFields =
                                r.post_draw_duplicate_config?.fields?.map((fId) => {
                                    return this.listBlockForm.find((b) => b.id == fId) || { id: fId, block_name: 'Unknown Field' };
                                }) || [];

                            return {
                                text: r.text,
                                quantity: r.quantity,
                                conditionQuery: '',
                                condition_config: r.condition_config || { condition: 'AND', filter_groups: [] },
                                typeFilter: r.condition_config?.condition || 'AND',
                                itemBlocks: itemBlocks.length ? itemBlocks : null,
                                preDrawFields: preDrawFields.length ? preDrawFields : [null],
                                postDrawFields: postDrawFields.length ? postDrawFields : [null],
                                fullDataDuplicateEnabled: r.full_data_duplicate_enabled || false,
                                candidateCount: null,
                                candidateList: [],
                                isLoadingPreview: false
                            };
                        });

                        this.currentTab = 'setting';
                    }
                }
            } catch (e) {
                console.error(e);
            } finally {
                this.isLoadingBasic = false;
            }
        },

        async saveAll(silent = false) {
            this.errors = {};
            if (!this.form.title) this.errors.title = 'タイトルを入力してください。';
            else if (this.form.title.length > 255) this.errors.title = 'タイトルを255文字以内で入力してください。';
            if (this.registrationType === 'internal' && !this.form.form_id) this.errors.form_id = 'フォームを選択してください。';
            if (this.registrationType === 'external' && !this.form.file && this.activeImportFile.length === 0)
                this.errors.file = 'ファイルをアップロードしてください。';

            if (Object.keys(this.errors).length > 0) {
                this.currentTab = 'basic';
                return false;
            }

            this.isSaving = true;
            try {
                const tags = this.configKeyword.arrayTag && this.configKeyword.arrayTag.length > 0 ? this.configKeyword.arrayTag : this.form.tags;
                const payload = {
                    id: this.lotteryId,
                    title: this.form.title,
                    source_type: this.registrationType === 'external' ? 'external' : 'form',
                    form_id: this.form.form_id?.id || this.form.form_id,
                    tags: tags
                };

                if (this.rewards && this.rewards.length > 0) {
                    payload.reward_config = {
                        rewards: this.rewards.map((r) => {
                            const conditionParsed =
                                r.condition_config ||
                                (r.conditionQuery ? this.parseConditionQuery(r.conditionQuery) : { condition: 'AND', filter_groups: [] });
                            return {
                                text: r.text,
                                quantity: r.quantity,
                                condition_config: conditionParsed,
                                pre_draw_duplicate_config: { fields: (r.preDrawFields || []).filter((f) => f?.id).map((f) => String(f.id)) },
                                post_draw_duplicate_config: {
                                    enabled: (r.postDrawFields || []).some((f) => f?.id),
                                    fields: (r.postDrawFields || []).filter((f) => f?.id).map((f) => String(f.id))
                                },
                                full_data_duplicate_enabled: r.fullDataDuplicateEnabled
                            };
                        })
                    };
                }

                await this.updateLottery(payload);

                if (!silent) {
                    showMessage(200, this.$bvToast, '保存しました。');
                }

                if (this.registrationType === 'external') {
                    if (this.form.file) {
                        const formData = new FormData();
                        formData.append('title', this.form.title);
                        formData.append('source_type', 'external');
                        tags.forEach((t, i) => formData.append(`tags[${i}]`, t));
                        formData.append('file', this.form.file);

                        const res = await this.acUpdateExternalLotteryFile({ id: this.lotteryId, formData });
                        const data = res?.data || res;
                        const importId = data.import?.id || data.import_id;

                        if (importId) {
                            this.pollImportStatus(importId);
                        } else {
                            await this.fetchExternalColumns();
                        }
                    } else {
                        await this.fetchExternalColumns();
                    }
                } else if (this.registrationType === 'internal') {
                    if (payload.form_id) {
                        const blocks = await this.getListBlockOfForm({ id: payload.form_id, query: 'exclude_deleted=true' });
                        this.listBlockForm = (blocks || [])
                            .sort((a, b) => a.order_index - b.order_index)
                            .map((b) => ({
                                ...b,
                                options: b.list_options,
                                submit_results: []
                            }));
                    }
                }
                return true;
            } catch (e) {
                console.error(e);
                if (!silent) showMessage(500, this.$bvToast, 'エラーが発生しました。');
                return false;
            } finally {
                this.isSaving = false;
            }
        },

        pollImportStatus(importId) {
            const timer = setInterval(async () => {
                try {
                    const res = await this.acGetExternalImportStatus({ lotteryId: this.lotteryId, importId });
                    const status = res?.data?.status || res?.status;
                    if (status === 'active' || status === 'success' || status === 'completed') {
                        clearInterval(timer);
                        try {
                            const resDetail = await this.detailLottery(this.lotteryId);
                            const data = resDetail?.data || resDetail;
                            if (data && data.active_import_file) {
                                this.activeImportFile = [
                                    {
                                        ...data.active_import_file,
                                        fileSize: Math.round((data.active_import_file.file_size || 0) / 1024)
                                    }
                                ];
                            }
                        } catch (err) {
                            console.error(err);
                        }
                        await this.fetchExternalColumns();
                        this.currentTab = 'setting';
                        this.isLoadingBasic = false;
                    } else if (status === 'failed' || status === 'error') {
                        clearInterval(timer);
                        this.isLoadingBasic = false;
                        showMessage(500, this.$bvToast, 'Import file thất bại');
                    }
                } catch (e) {
                    console.error(e);
                    clearInterval(timer);
                    this.isLoadingBasic = false;
                }
            }, 3000);
        },

        async fetchExternalColumns() {
            try {
                const res = await this.acGetExternalDataColumns(this.lotteryId);
                const columns = res?.data || res || [];
                this.listBlockForm = columns.map((c) => ({
                    id: c,
                    block_name: c,
                    block_type: 'input',
                    options: [],
                    submit_results: []
                }));
            } catch (e) {
                console.error(e);
            }
        },

        parseConditionQuery(queryString) {
            if (!queryString) return { condition: 'AND', filter_groups: [] };

            let condition = 'AND';
            const params = new URLSearchParams(queryString);
            const groups = {};

            for (const [key, val] of params.entries()) {
                if (key === 'searchCriteria[filterGroups][condition]') {
                    condition = val;
                } else {
                    const match = key.match(/searchCriteria\[filterGroups\]\[(\d+)\]\[([^\]]+)\]\[(\d+)\]\[([^\]]+)\]/);
                    if (match) {
                        const groupIdx = parseInt(match[1]);
                        const arrayName = match[2];
                        const itemIdx = parseInt(match[3]);
                        const prop = match[4];

                        if (!groups[groupIdx]) groups[groupIdx] = {};
                        if (!groups[groupIdx][arrayName]) groups[groupIdx][arrayName] = [];
                        if (!groups[groupIdx][arrayName][itemIdx]) groups[groupIdx][arrayName][itemIdx] = {};

                        const finalVal = val === 'true' ? true : val === 'false' ? false : val;

                        if (key.endsWith('[]')) {
                            if (!groups[groupIdx][arrayName][itemIdx][prop]) {
                                groups[groupIdx][arrayName][itemIdx][prop] = [];
                            }
                            groups[groupIdx][arrayName][itemIdx][prop].push(finalVal);
                        } else {
                            groups[groupIdx][arrayName][itemIdx][prop] = finalVal;
                        }
                    }
                }
            }

            const filterGroupsArr = Object.keys(groups)
                .sort((a, b) => a - b)
                .map((k) => {
                    const group = groups[k];
                    const res = {};
                    if (group.filter_blocks) res.filter_blocks = group.filter_blocks.filter(Boolean);
                    if (group.filter_result) res.filter_result = group.filter_result.filter(Boolean);
                    return res;
                });

            return { condition, filter_groups: filterGroupsArr };
        },

        async handleSubmitThenDraw(rewardIndex) {
            if (this.$refs.resultTab) {
                this.$refs.resultTab.executeDraw(rewardIndex);
            }
        }
    }
};
</script>
